/**
 * GizmoManager - PlayCanvas Gizmo wrapper for editor
 *
 * Provides translate, rotate, and scale gizmos for 3D object manipulation.
 * API modeled after BabylonJS GizmoManager for easier migration.
 */
import * as pc from 'playcanvas';
interface GizmoNamespaceWithLayer {
    createLayer: (app: pc.Application) => pc.Layer;
}
export type GizmoMode = 'translate' | 'rotate' | 'scale' | 'none';
export interface GizmoManagerConfig {
    snap?: boolean;
    snapIncrement?: number;
    coordSpace?: 'world' | 'local';
}
export interface TransformEvent {
    entity: pc.Entity | null;
    position?: pc.Vec3;
    rotation?: pc.Quat;
    scale?: pc.Vec3;
}
export class GizmoManager {
    private app: pc.Application;
    private camera: pc.CameraComponent;
    private gizmoLayer: pc.Layer;
    private translateGizmo: pc.TranslateGizmo | null = null;
    private rotateGizmo: pc.RotateGizmo | null = null;
    private scaleGizmo: pc.ScaleGizmo | null = null;
    private activeGizmo: pc.TranslateGizmo | pc.RotateGizmo | pc.ScaleGizmo | null = null;
    private attachedEntity: pc.Entity | null = null;
    private _mode: GizmoMode = 'none';
    private _interacting: boolean = false;
    // Event callbacks
    private onTransformStart?: (event: TransformEvent) => void;
    private onTransformMove?: (event: TransformEvent) => void;
    private onTransformEnd?: (event: TransformEvent) => void;
    private onPointerDown?: () => void;
    private onPointerUp?: () => void;
    constructor(app: pc.Application, camera: pc.CameraComponent, config: GizmoManagerConfig = {}) {
        this.app = app;
        this.camera = camera;
        // Create the gizmo layer
        this.gizmoLayer = (pc.Gizmo as unknown as GizmoNamespaceWithLayer).createLayer(app);
        // Create all gizmos
        this.translateGizmo = new pc.TranslateGizmo(camera, this.gizmoLayer);
        this.rotateGizmo = new pc.RotateGizmo(camera, this.gizmoLayer);
        this.scaleGizmo = new pc.ScaleGizmo(camera, this.gizmoLayer);
        // Apply config
        this.setSnap(config.snap ?? false, config.snapIncrement ?? 1);
        this.setCoordSpace(config.coordSpace ?? 'world');
        // Wire up events
        this.setupEvents(this.translateGizmo);
        this.setupEvents(this.rotateGizmo);
        this.setupEvents(this.scaleGizmo);
    }
    /**
     * Whether the user is currently interacting with a gizmo handle (dragging).
     * Use this to skip entity selection during gizmo drags.
     */
    /** The entity the active gizmo is attached to, if any. */
    get attached(): pc.Entity | null {
        return this.attachedEntity;
    }

    get isInteracting(): boolean {
        return this._interacting;
    }
    private setupEvents(gizmo: pc.TranslateGizmo | pc.RotateGizmo | pc.ScaleGizmo): void {
        // pointer:down fires with (x, y, meshInstance) — meshInstance is truthy
        // only when the pointer is actually over a gizmo handle. We must check
        // this to avoid suppressing camera input on every click in the viewport.
        gizmo.on('pointer:down', (_x: number, _y: number, meshInstance: unknown) => {
            if (meshInstance) {
                this._interacting = true;
                this.onPointerDown?.();
            }
        });
        gizmo.on('pointer:up', () => {
            if (this._interacting) {
                this._interacting = false;
                this.onPointerUp?.();
            }
        });
        gizmo.on('transform:start', () => {
            this.onTransformStart?.({
                entity: this.attachedEntity,
                position: this.attachedEntity?.getPosition().clone(),
                rotation: this.attachedEntity?.getRotation().clone(),
                scale: this.attachedEntity?.getLocalScale().clone()
            });
        });
        gizmo.on('transform:move', () => {
            this.onTransformMove?.({
                entity: this.attachedEntity,
                position: this.attachedEntity?.getPosition().clone(),
                rotation: this.attachedEntity?.getRotation().clone(),
                scale: this.attachedEntity?.getLocalScale().clone()
            });
        });
        gizmo.on('transform:end', () => {
            this.onTransformEnd?.({
                entity: this.attachedEntity,
                position: this.attachedEntity?.getPosition().clone(),
                rotation: this.attachedEntity?.getRotation().clone(),
                scale: this.attachedEntity?.getLocalScale().clone()
            });
        });
    }
    /**
     * Get the gizmo rendering layer (must be added to camera layers for visibility)
     */
    getLayer(): pc.Layer {
        return this.gizmoLayer;
    }
    /**
     * Get current gizmo mode
     */
    get mode(): GizmoMode {
        return this._mode;
    }
    /**
     * Set the active gizmo mode
     */
    setMode(mode: GizmoMode): void {
        this._mode = mode;
        // Detach current gizmo
        this.activeGizmo?.detach();
        this.activeGizmo = null;
        // Activate the new gizmo
        switch (mode) {
            case 'translate':
                this.activeGizmo = this.translateGizmo;
                break;
            case 'rotate':
                this.activeGizmo = this.rotateGizmo;
                break;
            case 'scale':
                this.activeGizmo = this.scaleGizmo;
                break;
            case 'none':
            default:
                break;
        }
        // Re-attach to current entity if any
        if (this.activeGizmo && this.attachedEntity) {
            this.activeGizmo.attach([this.attachedEntity]);
        }
    }
    /**
     * Whether the gizmo is currently attached to an entity.
     */
    get hasAttachment(): boolean {
        return this.attachedEntity !== null;
    }
    /**
     * Attach gizmo to an entity
     */
    attach(entity: pc.Entity | null): void {
        this.attachedEntity = entity;
        if (this.activeGizmo) {
            if (entity) {
                this.activeGizmo.attach([entity]);
            }
            else {
                this.activeGizmo.detach();
            }
        }
    }
    /**
     * Attach to mesh by finding the parent entity
     * (Compatibility with BabylonJS pattern)
     */
    attachToMesh(mesh: pc.Entity | null): void {
        this.attach(mesh);
    }
    /**
     * Detach gizmo from current entity
     */
    detach(): void {
        this.attachedEntity = null;
        this.activeGizmo?.detach();
    }
    /**
     * Enable/disable position gizmo
     * (BabylonJS compatibility)
     */
    set positionGizmoEnabled(enabled: boolean) {
        if (enabled && this._mode !== 'translate') {
            this.setMode('translate');
        }
        else if (!enabled && this._mode === 'translate') {
            this.setMode('none');
        }
    }
    get positionGizmoEnabled(): boolean {
        return this._mode === 'translate';
    }
    /**
     * Enable/disable rotation gizmo
     * (BabylonJS compatibility)
     */
    set rotationGizmoEnabled(enabled: boolean) {
        if (enabled && this._mode !== 'rotate') {
            this.setMode('rotate');
        }
        else if (!enabled && this._mode === 'rotate') {
            this.setMode('none');
        }
    }
    get rotationGizmoEnabled(): boolean {
        return this._mode === 'rotate';
    }
    /**
     * Enable/disable scale gizmo
     * (BabylonJS compatibility)
     */
    set scaleGizmoEnabled(enabled: boolean) {
        if (enabled && this._mode !== 'scale') {
            this.setMode('scale');
        }
        else if (!enabled && this._mode === 'scale') {
            this.setMode('none');
        }
    }
    get scaleGizmoEnabled(): boolean {
        return this._mode === 'scale';
    }
    /**
     * Set snap enabled and increment
     */
    setSnap(enabled: boolean, increment?: number): void {
        if (this.translateGizmo) {
            this.translateGizmo.snap = enabled;
            if (increment !== undefined) {
                this.translateGizmo.snapIncrement = increment;
            }
        }
        if (this.rotateGizmo) {
            this.rotateGizmo.snap = enabled;
            if (increment !== undefined) {
                this.rotateGizmo.snapIncrement = increment;
            }
        }
        if (this.scaleGizmo) {
            this.scaleGizmo.snap = enabled;
            if (increment !== undefined) {
                this.scaleGizmo.snapIncrement = increment;
            }
        }
    }
    /**
     * Set coordinate space (world or local)
     */
    setCoordSpace(space: 'world' | 'local'): void {
        if (this.translateGizmo) {
            this.translateGizmo.coordSpace = space;
        }
        if (this.rotateGizmo) {
            this.rotateGizmo.coordSpace = space;
        }
        if (this.scaleGizmo) {
            this.scaleGizmo.coordSpace = space;
        }
    }
    /**
     * Set transform event callbacks
     */
    onTransform(callbacks: {
        start?: (event: TransformEvent) => void;
        move?: (event: TransformEvent) => void;
        end?: (event: TransformEvent) => void;
        pointerDown?: () => void;
        pointerUp?: () => void;
    }): void {
        this.onTransformStart = callbacks.start;
        this.onTransformMove = callbacks.move;
        this.onTransformEnd = callbacks.end;
        this.onPointerDown = callbacks.pointerDown;
        this.onPointerUp = callbacks.pointerUp;
    }
    /**
     * Update gizmo (call each frame if needed)
     */
    update(): void {
        // PlayCanvas gizmos auto-update, but we can force refresh if needed
        this.activeGizmo?.update();
    }
    /**
     * Destroy the gizmo manager and clean up resources
     */
    destroy(): void {
        this.translateGizmo?.destroy();
        this.rotateGizmo?.destroy();
        this.scaleGizmo?.destroy();
        this.translateGizmo = null;
        this.rotateGizmo = null;
        this.scaleGizmo = null;
        this.activeGizmo = null;
        this.attachedEntity = null;
    }
}
